import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { googleAdsAccountAtom, campaignsDataAtom, organizationDataAtom } from '@/store';
import {
  businessQueryOptions,
  useCampaignAllStateApproveMutationOptions,
  useCampaignBudgetMutationOptions,
} from '@/lib/query-options';
import { validateCampaignAssets } from '@/lib/apis';
import { ownsTransitionSpinner } from '@/lib/transition-ownership';
import { useToast } from '@/hooks/useToast';
import { useCampaignErrorToast } from '@/hooks/useCampaignErrorToast';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import { usePaymentGate } from '@/hooks/usePaymentGate';
import { useCampaignDraftFields } from '@/hooks/useCampaignDraft';
import { useCampaignStateTransition } from '@/hooks/useCampaignStateTransition';
import { CAMPAIGN_STATE, type CampaignData } from '@/lib/types';

/**
 * Encapsulates the full go-live sequence for a Google campaign.
 *
 * Replaces the 3-step approval flow (validate assets → approve budget → dispatch LIVE).
 * All three steps are combined into a single button click.
 */
export function useGoLive(campaign: CampaignData) {
  const { toast } = useToast();
  const campaignErrorToast = useCampaignErrorToast();
  const refetchCampaign = useRefetchCampaign(campaign.slug);
  const { draft } = useCampaignDraftFields();
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const allCampaigns = useAtomValue(campaignsDataAtom);
  const organizationData = useAtomValue(organizationDataAtom);
  const [isRunning, setIsRunning] = useState(false);

  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  // Remaining daily budget from platform allocation (null = no allocation configured)
  const remainingDailyBudget = useMemo(() => {
    const googlePlatform = business?.platform_budgets?.find((b: { platform: string }) => b.platform === 'Google');
    if (!googlePlatform) return null;
    const platformMonthly = googlePlatform.amount;
    const otherCampaignsMonthly = allCampaigns
      .filter(c => c.id !== campaign.id)
      .reduce((sum, c) => sum + (c.budget || 0) * 30, 0);
    return Math.floor((platformMonthly - otherCampaignsMonthly) / 30);
  }, [business?.platform_budgets, allCampaigns, campaign.id]);

  const campaignAllStateApproveMutation = useCampaignAllStateApproveMutationOptions(campaign.slug);
  const campaignBudgetMutation = useCampaignBudgetMutationOptions();
  const { dispatch: dispatchStateChange, isPending: isStateTransitionPending, pendingSource } =
    useCampaignStateTransition(campaign.id, campaign.state as CAMPAIGN_STATE | undefined, 'go_live_button');
  const { PaymentGate, requirePayment } = usePaymentGate({ source: 'campaign_deploy' });

  const finalBudget = draft?.budget ?? campaign.budget;
  const hasNoAds = !campaign.ads || campaign.ads.length === 0;
  // Radius (proximity) targets are valid geo-targeting on their own — the backend
  // validate endpoint accepts either kind (#5446), so the pre-check must too (#5666).
  const hasNoLocations =
    (!campaign.target_locations || campaign.target_locations.length === 0) &&
    (!campaign.proximity_targets || campaign.proximity_targets.length === 0);
  const isCurrencyMissing = !organizationData?.default_currency;
  const isAccountAllowedToPublish = googleAdsAccount?.is_allowed_to_publish !== false;
  const isBudgetOverPlatform = remainingDailyBudget != null && !!finalBudget && finalBudget > remainingDailyBudget;

  const blockingReason: string | null = (() => {
    if (hasNoAds) return 'Add at least one ad before going live.';
    if (hasNoLocations) return 'Add at least one location before going live.';
    if (isCurrencyMissing) return 'Set your organisation currency before going live.';
    if (!isAccountAllowedToPublish) return 'This Google Ads account is not enabled for publishing.';
    if (!finalBudget || finalBudget <= 0) return 'Set a daily budget before going live.';
    if (isBudgetOverPlatform) return `Budget exceeds platform allocation. Max: ${Math.max(0, remainingDailyBudget ?? 0).toLocaleString()}/day.`;
    return null;
  })();

  // Spin only for launches this button dispatched — the transition polling is shared per
  // campaign, so a Deploy-initiated push must not light up Go Live as well (#5663).
  const isGoingLive = isRunning || (isStateTransitionPending && ownsTransitionSpinner('go_live_button', pendingSource));

  const goLive = async () => {
    // The button stays enabled when a precondition fails (#4805) — the click is the moment
    // to tell the user what to fix, not a silently disabled button.
    if (blockingReason) {
      toast({ description: blockingReason, variant: 'destructive' });
      return;
    }

    setIsRunning(true);
    try {
      await validateCampaignAssets(campaign.id);
      await campaignAllStateApproveMutation.mutateAsync();
      await campaignBudgetMutation.mutateAsync({
        campaignId: campaign.id,
        budgetUpdate: {
          budget: finalBudget!,
          target_cpa: draft?.targetCpa ?? 0,
          bidding_strategy_type: draft?.biddingStrategyType ?? campaign.bidding_strategy_type,
          approve: true,
        },
      });
      requirePayment(() => {
        void dispatchStateChange(CAMPAIGN_STATE.LIVE);
      });
      await refetchCampaign();
    } catch (error: unknown) {
      await refetchCampaign();
      campaignErrorToast({
        title: "Couldn't go live",
        message: error instanceof Error ? error.message : 'Please try again.',
        campaign,
      });
    } finally {
      setIsRunning(false);
    }
  };

  return {
    goLive,
    isGoingLive,
    blockingReason,
    PaymentGate,
  };
}
